import { __getTestCommandResolver } from './index'
import constructCommandResolver from './command-resolver'
import environment from 'customer-flows/config/environment'

interface CommandOptions {
  /**
   * Run immediately if no previous invocation.
   * Queue newest invocation to run after debounce timeout.
   * If already queued, new invocation replaces the queued one, retaining the previous invocation's debounce timeout.
   */
  debounce?: number
  /**
   * New invocation starts immediately. Previous invocation's result is discarded.
   */
  restartable?: boolean
  /**
   * If a previous invocation is still running drop the new invocation.
   */
  drop?: boolean
}

interface ConcurrencyState {
  lastInvocationId: number
  active: boolean
  lastRunTimestamp: number
  timeoutId?: ReturnType<typeof setTimeout>
  pendingPromise?: Promise<any>
  pendingResolve?: (value: any) => void
  pendingReject?: (error: any) => void
}

const ConcurrencyStateCache = new WeakMap<object, Map<string, ConcurrencyState>>()

/**
 * Decorator to inject a command into a specified property with optional concurrency behavior.
 *
 * Syntax:
 *  - `@command('command') specificMethod`
 *  - `@command('command:specificMethod') alias`
 *  - `@command('command:*') entireModule`
 *  - `@command('command', { debounce: 200, restartable: true }) specificMethod`
 *
 * Supports options like debounce, drop, and restartable.
 *
 * Commands can be found at `customer-flows/commands`.
 */
export function command(modulePath: CommandPath | CommandModuleName, options?: CommandOptions): any {
  return function decorator(_target: unknown, propertyKey: string) {
    return {
      get(): CommandFunction | CommandModule {
        return resolveCommand(this, modulePath, propertyKey, options)
      }
    }
  }
}

export function resolveCommand(context: any, modulePath: string, propertyKey?: string, options?: CommandOptions) {
  let [commandModule, commandName] = modulePath.split(':') as [string, string]
  commandName = commandName || propertyKey || ''

  const resolver = __getTestCommandResolver() || constructCommandResolver(createDispatch)
  const commands: CommandFunction = resolver.call(context, commandModule, commandName)

  if (!commands[commandModule]) {
    throw new Error(`"${commandName}" could not be resolved in command module "${commandModule}".`)
  }

  if (commandName === '*') {
    return commands[commandModule] // Return the entire module for wildcards
  }

  const originalCommand = commands[commandModule][commandName]

  if (typeof originalCommand !== 'function') {
    throw new Error(`"${commandName}" is not a function in command module "${commandModule}".`)
  }

  //TODO: properly integrate concurrency in test env
  if (options && !environment.isTesting) {
    const state = ConcurrencyStateCache.get(context) ?? new Map()
    ConcurrencyStateCache.set(context, state)

    if (!state.has(commandName)) {
      state.set(commandName, { lastInvocationId: 0, active: false, lastRunTimestamp: 0 })
    }

    return wrapWithConcurrencyControl(originalCommand, options, state.get(commandName)!)
  }

  return originalCommand
}

export const createDispatch = (context: any): CommandDispatcher => {
  return (path: CommandPath, ...args: any[]) => {
    // Note: no options → internal command calls are "plain" calls,
    // not governed by the decorator's concurrency options.
    const fn = resolveCommand(context, path)
    return fn(...args)
  }
}

function wrapWithConcurrencyControl<T extends (..._args: any[]) => any>(
  fn: T,
  options: CommandOptions,
  state: ConcurrencyState
): T {
  return function (this: any, ...args: Parameters<T>): ReturnType<T> {
    const now = Date.now()
    const debounceDelay = options.debounce ?? 0

    const run = (args: Parameters<T>) => {
      const invocationId = ++state.lastInvocationId

      if (options.drop && state.active) {
        return undefined as ReturnType<T>
      }

      state.active = true
      state.lastRunTimestamp = now
      state.timeoutId = undefined

      // ensure we're always working with a promise, even if the original function is synchronous
      const result = Promise.resolve(fn.apply(this, args)) as Promise<any>

      return result
        .then((res) => {
          if (!options.restartable || invocationId === state.lastInvocationId) {
            return res
          }
          return undefined
        })
        .finally(() => {
          if (invocationId === state.lastInvocationId) {
            state.active = false
          }
        }) as ReturnType<T>
    }

    // here we debounce only if method has been debounced by its command caller
    if (debounceDelay > 0) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId)
      }

      // create a new promise if we don't have one, or reuse the existing one
      if (!state.pendingPromise) {
        state.pendingPromise = new Promise((resolve, reject) => {
          state.pendingResolve = resolve
          state.pendingReject = reject
        })
      }

      state.timeoutId = setTimeout(() => {
        const promise = run.call(this, args)
        const resolve = state.pendingResolve
        const reject = state.pendingReject

        // clear the pending promise state
        state.pendingPromise = undefined
        state.pendingResolve = undefined
        state.pendingReject = undefined

        // resolve/reject the pending promise with the result of the actual work
        promise.then(resolve, reject)
      }, debounceDelay)

      return state.pendingPromise as ReturnType<T>
    }

    // no debounce timeout, run immediately
    return run(args)
  } as T
}
